import type {
  CertificatePlaceholder,
  ContentChangeKind,
  ContentReportReason,
  ContentReportStatus,
  ContentReportTarget,
  ContentReviewStatus,
} from '@mlm/shared';
import { api } from './api';
import { downloadBlob } from './download';

/** Admin ↔ API client for the LMS creator system (mirror of merchants-api.ts). */

export type AdminCreatorRow = {
  userId: string;
  displayName: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  ownerEmail: string;
  ownerName: string | null;
  platformFeePct: string | null;
  /** HOUSE account: the tenant's own staff, selling as house products. */
  isHouse: boolean;
  createdAt: string;
  /** Storefront-visible published courses. */
  courseCount: number;
  /** Distinct learners with a LIVE enrollment in any of them. */
  studentCount: number;
};

/**
 * Live per-creator statistics — the same payload the PUBLIC instructor page
 * renders, so admin and learner never see different numbers.
 */
export type CreatorStats = {
  creatorUserId: string;
  displayName: string;
  isHouse: boolean;
  joinedAt: string;
  courseCount: number;
  videoCount: number;
  r2VideoCount: number;
  studentCount: number;
  /** Null under `lms_subscription_model='tenant'` — not applicable, not zero. */
  subscriberCount: number | null;
  followers: number;
  rating: { average: number; count: number };
  transactions: number;
};

export type AdminCreatorDetail = {
  creator: {
    id: string;
    userId: string;
    displayName: string;
    bio: string | null;
    avatarKey: string | null;
    links: { label: string; url: string }[];
    platformFeePct: string | null;
    showFeeToCreator: boolean;
    isHouse: boolean;
    status: string;
    rejectionReason: string | null;
    approvedAt: string | null;
    createdAt: string;
    ownerEmail: string;
    ownerName: string | null;
  };
  submissions: Array<{
    id: string;
    requirementKey: string;
    fileKey: string | null;
    textValue: string | null;
    status: 'pending' | 'approved' | 'rejected';
    rejectionReason: string | null;
  }>;
  wallet: { available: string; pending: string; lifetimeEarned: string; updatedAt: string | null };
  /** Null until the creator is APPROVED (no public page, nothing to count). */
  stats: CreatorStats | null;
};

export type Paged<T> = { rows: T[]; total: number; page: number; pageSize: number };

export async function listCreators(params: {
  status?: string;
  page?: number;
  pageSize?: number;
  search?: string;
}) {
  return (await api.get<Paged<AdminCreatorRow>>('/admin/creators', { params })).data;
}
export async function getCreator(userId: string) {
  return (await api.get<AdminCreatorDetail>(`/admin/creators/${userId}`)).data;
}
export type CreateCreatorArgs = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  displayName: string;
  bio?: string | null;
  platformFeePct?: string;
  showFeeToCreator?: boolean;
  /** HOUSE account (tenant staff): provisioned APPROVED, no platform fee. */
  isHouse?: boolean;
};
export async function createCreator(args: CreateCreatorArgs) {
  return (await api.post<{ userId: string; status: string }>('/admin/creators', args)).data;
}
export async function updateCreatorCommission(
  userId: string,
  args: { platformFeePct?: string; showFeeToCreator?: boolean; isHouse?: boolean },
) {
  return (await api.patch<{ userId: string }>(`/admin/creators/${userId}`, args)).data;
}
export async function approveCreator(
  userId: string,
  args: { platformFeePct: string; showFeeToCreator?: boolean },
) {
  return (await api.post(`/admin/creators/${userId}/approve`, args)).data;
}
export async function rejectCreator(userId: string, reason?: string) {
  return (await api.post(`/admin/creators/${userId}/reject`, { reason })).data;
}
export async function suspendCreator(userId: string, reason?: string) {
  return (await api.post(`/admin/creators/${userId}/suspend`, { reason })).data;
}
export async function reactivateCreator(userId: string) {
  return (await api.post(`/admin/creators/${userId}/reactivate`)).data;
}
export async function reviewCreatorRequirement(
  submissionId: string,
  decision: 'approved' | 'rejected',
  rejectionReason?: string,
) {
  return (
    await api.post(`/admin/creator-requirements/${submissionId}/review`, {
      decision,
      rejectionReason,
    })
  ).data;
}
export async function creatorRequirementFileUrl(submissionId: string) {
  return (await api.get<{ url: string }>(`/admin/creator-requirements/${submissionId}/file-url`))
    .data;
}
export async function adjustCreatorLedger(
  userId: string,
  args: { amount: string; direction: 'C' | 'D'; reason: string },
) {
  return (await api.post(`/admin/creators/${userId}/adjust`, args)).data;
}

// ── courses (all courses; creator listing approval) ─────────────────────────
export type AdminCourseRow = {
  id: string;
  productId: string;
  productName: string;
  productActive: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  price: string;
  creatorUserId: string | null;
  creatorName: string | null;
  creatorIsHouse: boolean;
  accessLevel: string;
  level: string | null;
  categoryId: string | null;
  categoryName: string | null;
  language: string | null;
  status: 'draft' | 'published';
  /**
   * Phase 8. Orthogonal to `approvalStatus`: that one answers "may we sell
   * this listing?", this one answers "is what we already sell still what we
   * approved?". A course can hold an approved listing and a rejected re-upload
   * at the same time, so the two never collapse into one badge.
   */
  contentReviewStatus: ContentReviewStatus;
  lessonCount: number;
  enrollmentCount: number;
  tags: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string;
};
export async function listAllCourses(
  params: {
    creator?: string;
    status?: string;
    /** 'house' = no creator OR a house creator; 'creator' = third-party. */
    scope?: 'house' | 'creator' | 'all';
    q?: string;
    page?: number;
    pageSize?: number;
  } = {},
) {
  return (await api.get<Paged<AdminCourseRow>>('/admin/courses', { params })).data;
}
export async function reviewCreatorCourse(
  id: string,
  decision: 'approved' | 'rejected',
  rejectionReason?: string,
  /**
   * Rule on the CONTENT in the same call. One edit can open both states at
   * once and the dialog shows one diff for both, so the reviewer presses
   * Approve once; the server settles both in one transaction.
   */
  opts?: { alsoContent?: boolean },
) {
  return (
    await api.post(`/admin/creator-courses/${id}/review`, {
      decision,
      rejectionReason,
      ...(opts?.alsoContent ? { alsoContent: true } : {}),
    })
  ).data;
}
export async function unpublishCourse(id: string) {
  return (await api.patch(`/admin/courses/${id}`, { status: 'draft' })).data;
}

export type AdminCourseDetail = {
  id: string;
  productId: string;
  productName: string;
  creatorUserId: string | null;
  accessLevel: string;
  status: string;
  contentReviewStatus: ContentReviewStatus;
  /** When the creator last touched playable content; null on a clear course. */
  contentChangedAt: string | null;
  /**
   * Phase 10. The peek is now the ONE review desk, so it carries the LISTING
   * state too. `contentApprovedAt === null` on a creator course means no human
   * has ever ruled on it — that is the "New course" first-submission case, and
   * it reads very differently from a diff of what changed since.
   */
  productApprovalStatus: 'pending' | 'approved' | 'rejected';
  productRejectionReason: string | null;
  contentApprovedAt: string | null;
  creatorIsHouse: boolean;
  enrollmentCount: number;
  chapters: Array<{
    id: string;
    title: string;
    lessons: Array<{
      id: string;
      title: string;
      sourceType: string;
      videoStatus: string;
      isPreview: boolean;
      durationSeconds: number | null;
      /**
       * `lms_content_rereview='hold'`: the source shown here is the STAGED
       * version awaiting this decision. Students are still on the approved one.
       */
      heldPending?: boolean;
      liveSourceType?: string;
    }>;
  }>;
  /** The public teaser. Never held — marketing is not paid content. */
  promo: {
    sourceType: 'embed' | 'r2';
    embedUrl: string | null;
    videoStatus: string;
    durationSeconds: number | null;
  } | null;
};
export async function getCourseDetail(id: string) {
  return (await api.get<AdminCourseDetail>(`/admin/courses/${id}`)).data;
}

/** Play the course's promo video from the review desk. */
export type AdminPromoPreview =
  | { kind: 'iframe'; src: string; expiresInSeconds?: number }
  | { kind: 'video'; src: string; expiresInSeconds?: number };
export async function openPromoForReview(courseId: string) {
  return (await api.post<AdminPromoPreview>(`/admin/courses/${courseId}/promo/open`)).data;
}

// ── course enrollments (the manual grant / revoke recovery tool) ────────────
export type CourseEnrollmentSource = 'purchase' | 'free' | 'subscriber' | 'admin_grant';

/** Roster row — the joined shape only the LIST route returns. */
export type AdminCourseEnrollmentRow = {
  id: string;
  userId: string;
  userName: string | null;
  source: CourseEnrollmentSource;
  orderId: string | null;
  orderCode: string | null;
  grantedAt: string;
  revokedAt: string | null;
  expiresAt: string | null;
  lessonsCompleted: number;
  lessonCount: number;
  progressPct: number;
  lastActivityAt: string | null;
  testsPassed: number;
  testsRequired: number;
  certificateIssuedAt: string | null;
};

/** Bare row echoed by grant / revoke — no joined member name or order code. */
export type CourseEnrollment = {
  id: string;
  courseId: string;
  userId: string;
  source: CourseEnrollmentSource;
  orderId: string | null;
  grantedAt: string;
  revokedAt: string | null;
  expiresAt: string | null;
};

export async function listCourseEnrollments(courseId: string) {
  return (await api.get<AdminCourseEnrollmentRow[]>(`/admin/courses/${courseId}/enrollments`)).data;
}
export async function grantCourseEnrollment(
  courseId: string,
  args: { userId: string; expiresAt?: string | null },
) {
  return (await api.post<CourseEnrollment>(`/admin/courses/${courseId}/enrollments`, args)).data;
}
export async function revokeCourseEnrollment(courseId: string, enrollmentId: string) {
  return (
    await api.post<CourseEnrollment>(
      `/admin/courses/${courseId}/enrollments/${enrollmentId}/revoke`,
    )
  ).data;
}

// ── Q&A moderation ─────────────────────────────────────────────────────────
export type AdminQuestionRow = {
  id: string;
  body: string;
  hidden: boolean;
  createdAt: string;
  authorName: string | null;
  lessonTitle: string;
  courseId: string;
  isCreatorReply: boolean;
  parentId: string | null;
};
export async function listAllQuestions(
  params: { page?: number; pageSize?: number; courseId?: string; q?: string } = {},
) {
  return (await api.get<Paged<AdminQuestionRow>>('/admin/course-questions', { params })).data;
}
export async function setQuestionHidden(id: string, hidden: boolean) {
  return (await api.post(`/admin/course-questions/${id}/hide`, { hidden })).data;
}

// ── payouts ────────────────────────────────────────────────────────────────
export type AdminCreatorPayoutRow = {
  id: string;
  creatorUserId: string;
  displayName: string;
  ownerEmail: string;
  amount: string;
  netAmount: string;
  method: string;
  status: string;
  payload: Record<string, unknown>;
  requestedAt: string;
};
export async function listCreatorPayouts(params: {
  status?: string;
  page?: number;
  pageSize?: number;
}) {
  return (await api.get<Paged<AdminCreatorPayoutRow>>('/admin/creator-payouts', { params })).data;
}
export async function markCreatorPayoutPaid(id: string) {
  return (await api.post(`/admin/creator-payouts/${id}/mark-paid`)).data;
}
export async function rejectCreatorPayout(id: string, reason?: string) {
  return (await api.post(`/admin/creator-payouts/${id}/reject`, { reason })).data;
}
export type AdminCreatorPayoutBatch = {
  id: string;
  periodStart: string;
  periodEnd: string;
  total: string;
  currency: string;
  status: string;
  createdAt: string;
  processedAt: string | null;
};
export async function createCreatorPayoutBatch(args: {
  periodStart: string;
  periodEnd: string;
  requestIds: string[];
}) {
  return (await api.post('/admin/creator-payouts/batches', args)).data;
}
export async function listCreatorPayoutBatches(params: { page?: number; pageSize?: number } = {}) {
  return (await api.get<AdminCreatorPayoutBatch[]>('/admin/creator-payouts/batches', { params }))
    .data;
}
export async function markCreatorPayoutBatchPaid(id: string) {
  return (await api.post(`/admin/creator-payouts/batches/${id}/mark-paid`)).data;
}
export async function rejectCreatorPayoutBatch(id: string, rejectionReason?: string) {
  return (await api.post(`/admin/creator-payouts/batches/${id}/reject`, { rejectionReason })).data;
}
export async function exportCreatorPayoutBatch(id: string) {
  const res = await api.get(`/admin/creator-payouts/batches/${id}/export`, {
    responseType: 'blob',
  });
  downloadBlob(res.data as BlobPart, `creator-payout-batch-${id}.csv`, 'text/csv');
}

// ── course categories (the course TOPIC registry — separate from product
// categories: a course's topic lives on the course row, not its product) ──
export type CourseCategory = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  /** One level of nesting only: a root has `null`, a child names its root. The
   *  depth rule is the server's — the UI just refuses to offer a child as a
   *  parent so an admin rarely meets the 400. */
  parentId: string | null;
  /** R2 key of the topic's picture — the photo on the /courses topic cards. */
  imageKey: string | null;
  createdAt?: string;
  updatedAt?: string;
};
export async function listCourseCategories() {
  return (await api.get<CourseCategory[]>('/admin/course-categories')).data;
}
export async function createCourseCategory(input: {
  name: string;
  sortOrder?: number;
  active?: boolean;
  parentId?: string | null;
}) {
  return (await api.post<CourseCategory>('/admin/course-categories', input)).data;
}
export async function updateCourseCategory(
  id: string,
  patch: Partial<{ name: string; sortOrder: number; active: boolean; parentId: string | null }>,
) {
  return (await api.patch<CourseCategory>(`/admin/course-categories/${id}`, patch)).data;
}
export async function deleteCourseCategory(id: string) {
  await api.delete(`/admin/course-categories/${id}`);
}

/**
 * A topic's picture. Served PUBLICLY by id (a browser <img> carries no
 * Authorization header), so the URL is stable — cache-bust it with the stored
 * key when replacing one.
 */
export function courseCategoryImageUrl(id: string) {
  return `${api.defaults.baseURL ?? ''}/storefront/course-categories/${id}/image`;
}
export async function uploadCourseCategoryImage(id: string, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return (
    await api.post<{ imageKey: string }>(`/admin/course-categories/${id}/image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ).data;
}
export async function removeCourseCategoryImage(id: string) {
  await api.delete(`/admin/course-categories/${id}/image`);
}

// ── course tags (the many-to-many labels a course carries BESIDE its single
// topic — flat by design, so no parent here) ──
export type CourseTag = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
};
export async function listCourseTags() {
  return (await api.get<CourseTag[]>('/admin/course-tags')).data;
}
export async function createCourseTag(input: {
  name: string;
  sortOrder?: number;
  active?: boolean;
}) {
  return (await api.post<CourseTag>('/admin/course-tags', input)).data;
}
export async function updateCourseTag(
  id: string,
  patch: Partial<{ name: string; sortOrder: number; active: boolean }>,
) {
  return (await api.patch<CourseTag>(`/admin/course-tags/${id}`, patch)).data;
}
export async function deleteCourseTag(id: string) {
  await api.delete(`/admin/course-tags/${id}`);
}

// ── course test grading (HOUSE courses — creator attempts grade in their portal) ──
export type PendingTestAttemptRow = {
  id: string;
  testId: string;
  testTitle: string;
  courseId: string;
  courseName: string;
  userId: string;
  memberName: string | null;
  attemptNumber: number;
  autoScore: number;
  maxScore: number;
  submittedAt: string;
};
export async function listPendingTestAttempts() {
  return (await api.get<PendingTestAttemptRow[]>('/admin/test-attempts')).data;
}
export type TestAttemptEntry = {
  questionId: string;
  kind: 'multiple_choice' | 'essay';
  prompt: string;
  choices: string[];
  correctIndex: number | null;
  points: number;
  selectedIndex?: number | null;
  text?: string | null;
  awardedPoints: number;
  comment?: string | null;
};
export type TestAttemptDetail = {
  id: string;
  entries: TestAttemptEntry[];
  autoScore: number;
  maxScore: number;
  status: string;
  submittedAt: string;
};
export async function getTestAttemptDetail(id: string) {
  return (await api.get<TestAttemptDetail>(`/admin/test-attempts/${id}`)).data;
}
export async function gradeTestAttempt(
  id: string,
  grades: { questionId: string; points: number; comment?: string | null }[],
) {
  return (
    await api.post<{
      attempt: { status: string; scorePct: number | null };
      certificateIssued: boolean;
    }>(`/admin/test-attempts/${id}/grade`, { grades })
  ).data;
}

// ── House certificate templates (creator_user_id NULL) ─────────────────────
// A house template and a creator's template are the same row in the same
// table; only the owner column differs. Assignment to a course stays in the
// creator portal, where house courses are authored.

export type AdminCertificateTemplate = {
  id: string;
  creatorUserId: string | null;
  name: string;
  imageKey: string;
  config: { placeholders: CertificatePlaceholder[] };
  createdAt: string;
  updatedAt: string;
};

export async function listCertificateTemplates() {
  return (await api.get<AdminCertificateTemplate[]>('/admin/certificate-templates')).data;
}

export async function createCertificateTemplate(
  name: string,
  file: File,
  config?: { placeholders: CertificatePlaceholder[] },
) {
  const fd = new FormData();
  fd.append('name', name);
  if (config) fd.append('config', JSON.stringify(config));
  fd.append('file', file);
  return (
    await api.post<AdminCertificateTemplate>('/admin/certificate-templates', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ).data;
}

/** Layout-only edits go as JSON; replacing the background goes as multipart. */
export async function updateCertificateTemplate(
  id: string,
  patch: { name?: string; config?: { placeholders: CertificatePlaceholder[] } },
  file?: File | null,
) {
  if (file) {
    const fd = new FormData();
    if (patch.name !== undefined) fd.append('name', patch.name);
    if (patch.config) fd.append('config', JSON.stringify(patch.config));
    fd.append('file', file);
    return (
      await api.patch<AdminCertificateTemplate>(`/admin/certificate-templates/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data;
  }
  return (await api.patch<AdminCertificateTemplate>(`/admin/certificate-templates/${id}`, patch))
    .data;
}

export async function deleteCertificateTemplate(id: string) {
  await api.delete(`/admin/certificate-templates/${id}`);
}

/** PUBLIC route — an `<img>` cannot carry the Authorization header. */
export function certificateTemplateImageUrl(id: string) {
  return `${api.defaults.baseURL ?? ''}/certificate-templates/${id}/image`;
}

// ── course CONTENT review (Phase 8) ─────────────────────────────────────────
// The second review queue. `reviewCreatorCourse` above rules on the LISTING
// (may we sell this?); everything here rules on the playable CONTENT of a
// course we already approved, which a creator can swap out at any time.

/** A course whose creator changed playable content since it was approved. */
export type ContentPendingCourseRow = {
  courseId: string;
  productId: string;
  name: string;
  status: 'draft' | 'published';
  approvalStatus: 'pending' | 'approved' | 'rejected';
  contentChangedAt: string | null;
  creatorUserId: string | null;
  creatorName: string | null;
  /** Unresolved change rows — how much reading the reviewer is in for. */
  changeCount: number;
};

/**
 * The before/after snapshot a change row carries. Deliberately NOT the lesson
 * itself: `videoKey` is reduced to the string 'set' server-side so the queue
 * can say "a video was there" without leaking an R2 object key, and an article
 * body arrives as a length. Every field is optional because the shape depends
 * on the change `kind`, and a create/delete has no counterpart side at all.
 */
export type ContentChangeSnapshot = {
  sourceType?: string | null;
  embedUrl?: string | null;
  videoKey?: 'set' | null;
  bodyLength?: number | null;
  /**
   * Phase 10 — tier-3 (editorial) rows carry the VALUES that moved, keyed
   * by field, symmetric across `before`/`after`. The key set depends on
   * what the creator edited, so this stays an open record; the renderer
   * decides how to present each key (word diff, chips, money, on/off).
   */
  [field: string]: unknown;
} | null;

export type CourseContentChangeRow = {
  id: string;
  lessonId: string | null;
  /** Snapshotted at write time — a deleted lesson still names itself here. */
  lessonTitle: string;
  kind: ContentChangeKind;
  /** 1 gates the review; 2 is context only; 3 gates only under scope 'all'. */
  tier: number;
  before: ContentChangeSnapshot;
  after: ContentChangeSnapshot;
  createdAt: string;
  resolvedAt: string | null;
  resolution: 'approved' | 'rejected' | null;
};

export async function listContentPendingCourses() {
  return (await api.get<ContentPendingCourseRow[]>('/admin/creator-courses/content-pending')).data;
}
export async function listCourseContentChanges(courseId: string) {
  return (await api.get<CourseContentChangeRow[]>(`/admin/courses/${courseId}/content-changes`))
    .data;
}
export async function reviewCourseContent(
  courseId: string,
  decision: 'approved' | 'rejected',
  reason?: string,
) {
  return (
    await api.post<{ courseId: string; changed: number }>(
      `/admin/creator-courses/${courseId}/content-review`,
      { decision, reason },
    )
  ).data;
}

/**
 * A reviewer's playable copy of one lesson — the SAME resolver the member
 * `/open` route uses, gated on the review permission instead of on enrollment.
 * Mirrors the API's `LessonSource` union: 'video' covers both an R2 upload and
 * a direct .mp4 embed, 'iframe' covers YouTube/Vimeo, 'article' the HTML body.
 *
 * It is a POST because it mints a short-lived signed URL, and it records
 * nothing — a reviewer watching is not a learner opening.
 */
export type AdminLessonPreview = { lessonId: string; title: string } & (
  | { kind: 'iframe'; src: string; expiresInSeconds?: number }
  | {
      kind: 'video';
      src: string;
      expiresInSeconds?: number;
      captions?: { id: string; lang: string; label: string; src: string }[];
    }
  | { kind: 'article'; body: string }
);
export async function openLessonForReview(courseId: string, lessonId: string) {
  return (await api.post<AdminLessonPreview>(`/admin/courses/${courseId}/lessons/${lessonId}/open`))
    .data;
}

// ── moderation queue (Phase 8) ──────────────────────────────────────────────
// Member-filed reports on course content. Lives here rather than in api.ts
// because every report is anchored to a course (`content_reports.course_id` is
// NOT NULL) — this is an LMS surface, not a general abuse inbox.

/** Paged envelope for the Phase 8 routes, which return `items`, not `rows`. */
export type PagedItems<T> = { items: T[]; total: number; page: number; pageSize: number };

export type ContentReportRow = {
  id: string;
  targetKind: ContentReportTarget;
  targetId: string;
  courseId: string;
  courseName: string;
  reason: ContentReportReason;
  details: string | null;
  status: ContentReportStatus;
  createdAt: string;
  resolvedAt: string | null;
  resolutionNote: string | null;
  reporterName: string | null;
  /** First 240 chars of the reported content; null for a `course` target. */
  excerpt: string | null;
  /** Null for a `course` target — a course has no hidden flag to read. */
  targetHidden: boolean | null;
  /** How many OPEN reports point at this same target, this row included. */
  openCount: number;
};

export async function listContentReports(
  params: {
    status?: ContentReportStatus;
    targetKind?: ContentReportTarget;
    page?: number;
    pageSize?: number;
  } = {},
) {
  return (await api.get<PagedItems<ContentReportRow>>('/admin/reports', { params })).data;
}
export async function decideContentReport(
  id: string,
  decision: 'resolve' | 'dismiss',
  note?: string,
) {
  return (await api.post(`/admin/reports/${id}/${decision}`, { note })).data;
}
export async function setReportTargetHidden(id: string, hidden: boolean) {
  return (await api.post(`/admin/reports/${id}/hide-target`, { hidden })).data;
}

// ── creator subscription plans (Phase 10) ───────────────────────────────────
// A third-party creator's plan product is born `pending` and a price edit
// re-opens it, exactly like a course listing. House plans never appear here.

export type PendingCreatorPlanRow = {
  planId: string;
  productId: string;
  period: 'monthly' | 'semi_annual' | 'annual';
  price: string;
  creatorUserId: string;
  creatorName: string | null;
  submittedAt: string;
};

export async function listPendingCreatorPlans() {
  return (await api.get<PendingCreatorPlanRow[]>('/admin/creator-subscription-plans/pending')).data;
}

export async function reviewCreatorPlan(
  planId: string,
  decision: 'approved' | 'rejected',
  reason?: string,
) {
  return (
    await api.post(`/admin/creator-subscription-plans/${planId}/review`, { decision, reason })
  ).data;
}
